import axios from "axios"
import Cookies from "js-cookie"

/**
 * Meeting scheduling (core.scheduling). Super-admin only.
 *
 * Deliberately not on `apiRequest`: that helper de-duplicates and caches
 * responses for five seconds, which is wrong for booking state, and the shared
 * axios instance signs the admin out on any 503 — a status this feature uses
 * for an ordinary "the calendar is busy right now". So this module owns a thin
 * instance with the same base URL and bearer token, and nothing else.
 */
const client = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || "",
})

client.interceptors.request.use((config) => {
  const token = Cookies.get("token")
  if (token) config.headers["Authorization"] = `Bearer ${token}`
  config.headers["Accept"] = "application/json"
  // No request-side cache header: it is not in the backend's allowed CORS
  // headers, so adding it fails the actual request after a passing preflight.
  // The responses already carry `Cache-Control: no-store`, which is what keeps
  // booking state out of caches.
  return config
})

/** A stable, unguessable id so a retry maps to the original attempt. */
/**
 * Returns "" where Web Crypto is unavailable: a guessable id would be another
 * caller's capability to read this booking, so the dialog stops instead.
 */
export const newRequestId = () => {
  const source = typeof window === "undefined" ? undefined : window.crypto
  if (!source?.getRandomValues) return ""
  const bytes = new Uint8Array(18)
  source.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
}

const ADMIN = "/api/admin/scheduling"

const Scheduling = {
  getSettings: () => client.get(`${ADMIN}/settings/`),

  /** Optimistic: `version` must match the row the admin was shown. */
  updateSettings: (payload) => client.patch(`${ADMIN}/settings/`, payload),

  /** Where setup returns to is backend configuration, not ours to send. */
  connectGoogle: () => client.post(`${ADMIN}/google/connect/`, {}),

  getSlots: () => client.get(`${ADMIN}/slots/`),

  publishSlot: ({ date, startTime }) =>
    client.post(`${ADMIN}/slots/`, { date, start_time: startTime }),

  setSlotPublication: (slotId, { isPublished, version }) =>
    client.patch(`${ADMIN}/slots/${slotId}/`, {
      is_published: isPublished,
      version,
    }),

  getBookings: ({ scope = "upcoming", status = "", dateFrom = "", dateTo = "", page = 1, pageSize = 10 } = {}) =>
    client.get(`${ADMIN}/bookings/`, {
      // An empty filter is left out rather than sent blank.
      params: {
        scope,
        status: status || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page,
        page_size: pageSize,
      },
    }),

  /** The same service the public modal uses; source and actor are set server-side. */
  createBooking: ({ slotId, slotVersion, name, email, message, timezone, requestId,
                   guestEmails }) =>
    client.post(`${ADMIN}/bookings/`, {
      slot_id: slotId,
      slot_version: slotVersion,
      attendee_name: name,
      attendee_email: email,
      attendee_message: message || "",
      timezone,
      request_id: requestId,
      guest_emails: guestEmails || [],
    }),

  getBooking: (bookingId, { refresh = false } = {}) =>
    client.get(`${ADMIN}/bookings/${bookingId}/${refresh ? "?refresh=true" : ""}`),

  cancelBooking: (bookingId) => client.post(`${ADMIN}/bookings/${bookingId}/cancel/`, {}),
}

export default Scheduling
